// src/app/data/api/DataFetcher.ts
import { NetworkUtil } from '@/app/network/NetworkUtil';
import { NetworkError } from '@/app/network/models/ApiResponse';
import { AuthApi } from '@/app/data/auth/AuthApi';

export class DataFetcher {
  private network = new NetworkUtil();
  private static refreshPromise: Promise<any> | null = null;

  async get<T>(path: string, params?: Record<string, string | number>): Promise<T> {
    return this._executeWithRetry<T>('GET', path, undefined, params);
  }

  async post<T>(
    path: string,
    body?: unknown,
    params?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Promise<T> {
    return this._executeWithRetry<T>('POST', path, body, params, headers);
  }

  async patch<T>(
    path: string,
    body?: unknown,
    params?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Promise<T> {
    return this._executeWithRetry<T>('PATCH', path, body, params, headers);
  }

  async delete<T>(
    path: string,
    params?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Promise<T> {
    return this._executeWithRetry<T>('DELETE', path, undefined, params, headers);
  }

  private async _executeWithRetry<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    params?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Promise<T> {
    try {
      return await this._request<T>(method, path, body, params, headers);
    } catch (error) {
      if (error instanceof NetworkError && error.code === 401) {
        if (!DataFetcher.refreshPromise) {
          DataFetcher.refreshPromise = this.tryRefreshToken()
            .finally(() => {
              DataFetcher.refreshPromise = null;
            });
        }
        try {
          await DataFetcher.refreshPromise;

          // После успешного refresh access token в localStorage обновился,
          // но WebSocket SDK сидит на старом сокете/токене. По доке нужно
          // переоткрыть соединение — accessToken() вернёт свежий JWT.
          // Dynamic import — чтобы не ловить циклическую зависимость
          // (OmegaWsService → ProfileViewModel → PaymentsApi → DataFetcher).
          void this.reconnectWsQuietly();

          return await this._request<T>(method, path, body, params, headers);
        } catch (refreshError) {
          AuthApi.clearTokens();
          throw error;
        }
      }
      throw error;
    }
  }

  private async reconnectWsQuietly(): Promise<void> {
    try {
      const mod = await import('@/sdk/OmegaWsService');
      const svc = mod.OmegaWsService.getInstance();
      const status = svc.getStatus();
      // Переоткрываем только если клиент уже был создан и был в работе.
      if (status === 'ready' || status === 'reconnecting' || status === 'connecting') {
        await svc.reconnect();
      }
    } catch {
      /* noop: WS ещё не поднят — не наша забота здесь */
    }
  }

  private async tryRefreshToken(): Promise<void> {
    const stored = localStorage.getItem('auth_tokens');
    if (!stored) throw new Error('No tokens');
    const tokens = JSON.parse(stored);
    if (!tokens.refreshToken) throw new Error('No refresh token');
    const authApi = new AuthApi();
    await authApi.refreshToken(tokens.refreshToken);
  }

  private async _request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    params?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Promise<T> {
    let response: any;
    switch (method) {
      case 'GET':
        response = await this.network.get<any>(path, params);
        break;
      case 'POST':
        response = await this.network.post<any>(path, body, params, headers);
        break;
      case 'PATCH':
        response = await this.network.patch<any>(path, body, params, headers);
        break;
      case 'DELETE':
        response = await this.network.delete<any>(path);
        break;
      default:
        throw new Error('Unsupported method');
    }

    if (response && typeof response === 'object' && 'data' in response) {
      return response.data as T;
    }
    throw new Error('Некорректный ответ сервера');
  }
}